'use client';

import dynamic from 'next/dynamic';
import { Image } from '@/components/ui/image/image';
import { DocumentCard } from '@/components/ui/document-card';
import type { MediaData } from '@/lib/getMediaData';
import { cn } from '@/lib/utils';
import styles from './media.module.scss';

const LottieComponent = dynamic(() => import('@/components/ui/card-with-image/lottie-component'), {
  ssr: false,
});

const RiveComponent = dynamic(() => import('@/components/ui/card-with-image/rive-component'), {
  ssr: false,
});

const HtmlEmbed = dynamic(() => import('./html-embed').then((m) => ({ default: m.HtmlEmbed })), {
  ssr: false,
});

export type MediaProps = MediaData & {
  /** Additional class name */
  className?: string;
  /** Image loading priority */
  priority?: boolean;
};

export const Media = (props: MediaProps) => {
  const {
    mediaType,
    src,
    alt,
    url,
    videoEmbedUrl,
    animationFormat,
    animationSrc,
    fallbackSrc,
    loop,
    autoplay,
    playAudio,
    lottieJson,
    embedCode,
    fileSrc,
    fileName,
    fileSize,
    fileType,
    description,
    className,
    priority,
  } = props;
  // Use explicit width/height prop if provided, otherwise fall back to asset dimensions
  const width = props.width ?? 800;
  const height = props.height ?? 500;
  switch (mediaType) {
    case 'Advanced Image':
      if (!src) return null;
      const image = (
        <Image
          src={src}
          alt={alt ?? ''}
          width={width}
          height={height}
          className={cn(!url && className)}
          priority={priority}
        />
      );
      if (url) {
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" className={className}>
            {image}
          </a>
        );
      }
      return image;

    case 'Video':
      if (!videoEmbedUrl) return null;
      return (
        <div className={cn(styles.video, className)}>
          <div className={styles.video__wrapper}>
            <iframe
              src={videoEmbedUrl}
              title="Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; compute-pressure"
              allowFullScreen
              className={styles.video__iframe}
            />
          </div>
        </div>
      );

    case 'Animation':
      if (!animationSrc && !lottieJson) {
        if (fallbackSrc) {
          return (
            <Image
              src={fallbackSrc}
              alt={alt ?? ''}
              width={width}
              height={height}
              className={className}
            />
          );
        }
        return null;
      }
      if (animationFormat === 'Rive') {
        if (!animationSrc) return null;
        return (
          <RiveComponent
            src={animationSrc}
            autoplay={autoplay}
            playAudio={playAudio}
            width={width}
            height={height}
            className={className}
          />
        );
      }
      // Lottie — prefer inline JSON, fall back to URL
      if (lottieJson) {
        return (
          <LottieComponent
            data={lottieJson}
            loop={loop}
            autoplay={autoplay}
            className={className}
          />
        );
      }
      if (!animationSrc) return null;
      return (
        <LottieComponent src={animationSrc} loop={loop} autoplay={autoplay} className={className} />
      );

    case 'Embed':
      if (!embedCode) return null;
      return <HtmlEmbed code={embedCode} className={className} />;

    case 'Document':
      if (!fileSrc || !fileName) return null;
      return (
        <DocumentCard
          fileSrc={fileSrc}
          fileName={fileName}
          fileType={fileType}
          fileSize={fileSize}
          description={description}
          className={className}
        />
      );

    default:
      return null;
  }
};
